import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API from '../api';
import { CustomerAccountSkeleton } from '../components/skeleton';
import './CustomerAccount.css';

const CustomerAccount = () => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    last_name: '',
    newsletter: false
  });

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        const response = await API.get('/customer/account');
        if (response.data.success) {
          const customerData = response.data.customer;
          setCustomer(customerData);
          setFormData({
            name: customerData.name || '',
            last_name: customerData.last_name || '',
            newsletter: customerData.newsletter === 1
          });
        } else {
          setError(response.data.message || 'Errore durante il recupero dei dati');
        }
      } catch (err) {
        console.error('Errore durante il recupero delle informazioni del cliente:', err);
        setError('Errore durante il recupero dei dati');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const response = await API.put('/customer/account', formData);
      if (response.data.success) {
        setCustomer(response.data.customer);
        setIsEditing(false);
        setMessage('Dati aggiornati con successo!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(response.data.message || 'Errore durante il salvataggio');
      }
    } catch (err) {
      console.error('Errore durante l\'aggiornamento:', err);
      setMessage(err.response?.data?.message || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      name: customer.name || '',
      last_name: customer.last_name || '',
      newsletter: customer.newsletter === 1
    });
    setMessage('');
  };

  if (loading) {
    return <CustomerAccountSkeleton />;
  }

  if (error) {
    return (
      <div className="main-content-container">
        <div className="error-message">
          <h2>Errore</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content-container">
      <div className="account-header">
        <h1>Il Mio Account</h1>
        <p className="account-subtitle">Gestisci le tue informazioni personali</p>
      </div>

      {message && (
        <div className={`message ${message.includes('successo') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="account-content">
        <div className="account-section">
          <div className="section-header">
            <h2>Informazioni Personali</h2>
            {!isEditing && (
              <button 
                className="edit-button"
                onClick={() => setIsEditing(true)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
                Modifica
              </button>
            )}
          </div>

          {isEditing ? (
            <form onSubmit={handleSubmit} className="edit-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Nome *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="last_name">Cognome *</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={customer.email}
                  disabled
                  className="form-input disabled"
                />
                <small>L'email non può essere modificata</small>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="newsletter"
                    checked={formData.newsletter}
                    onChange={handleInputChange}
                    className="checkbox-input"
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-text">
                    Iscrivimi alla newsletter per ricevere offerte e novità
                  </span>
                </label>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleCancel}
                  className="cancel-button"
                  disabled={saving}
                >
                  Annulla
                </button>
                <button 
                  type="submit" 
                  className="save-button"
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : 'Salva Modifiche'}
                </button>
              </div>
            </form>
          ) : (
            <div className="info-display">
              <div className="info-row">
                <div className="info-group">
                  <label>Nome</label>
                  <p>{customer.name || 'Non specificato'}</p>
                </div>
                <div className="info-group">
                  <label>Cognome</label>
                  <p>{customer.last_name || 'Non specificato'}</p>
                </div>
              </div>
              
              <div className="info-group">
                <label>Email</label>
                <p>{customer.email}</p>
              </div>

              <div className="info-group">
                <label>Newsletter</label>
                <p className={customer.newsletter === 1 ? 'enabled' : 'disabled'}>
                  {customer.newsletter === 1 ? 'Iscritto' : 'Non iscritto'}
                </p>
              </div>

              <div className="info-group">
                <label>Account creato il</label>
                <p>{new Date(customer.created_at).toLocaleDateString('it-IT')}</p>
              </div>
            </div>
          )}
        </div>

        <div className="account-section">
          <h2>Azioni Account</h2>
          <div className="actions-grid">
            <div className="action-card">
              <div className="action-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
              </div>
              <h3>La tua Loverlist</h3>
              <p>Gestisci i tuoi prodotti preferiti</p>
              <Link to="/wishlist" className="action-button">
                Vedi tutti 
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerAccount;
